import axios, { AxiosError, type AxiosResponse } from "axios"
import { ElMessage, ElMessageBox } from "element-plus"
import { ResponseCodeEnum, StorageKeysEnum } from "@/constants"
import router from "@/router"
import { buildHeaders, dateToString } from "@/utils"

const instance = axios.create({
  baseURL: process.env.VUE_APP_BASE_URL,
  timeout: 1000 * 60
})

const ignoreAuthenticationRequestList = ["/api/common/ad/login"]

instance.interceptors.request.use(
  (config: any) => {
    // 如果当前请求的 api 地址是不需要鉴权的 api, 那么直接返回 config, 不需要再额外添加 headers
    if (ignoreAuthenticationRequestList.some((value) => value === config.url)) {
      return config
    }
    
    // 如果数据中有有关时间的数据, axios 会自动将 Date 转成 string 发给后端
    // 但是前端生成签名的时候会自动使用 encodeURIComponent 将字符串的特殊符号进行转义, 如果 value 不是字符串的数据, 估计会自动调用该对象 toString 方法
    // 这就导致前后端的时间数据虽然相同, 但是时间格式不相同, 就会导致生成出来的签名不一样, 出现签名校验失败
    config.params = dateToString(config.params)
    config.data = dateToString(config.data)
    
    config.headers = {
      ...buildHeaders(config),
      ...config.headers
    }
    
    return config
  },
  error => Promise.reject(error)
)

const ERROR_MAP: Record<string, Function> = {
  [AxiosError.ERR_NETWORK]: () => {
    ElMessage({
      type: "error",
      showClose: true,
      message: "无法连接网络"
    })
  }
}

const handleError = (error: AxiosError) => {
  if (error.code) {
    ERROR_MAP[error.code]?.()
  }
}

let isTokenInvalid = false

instance.interceptors.response.use(
  (response: AxiosResponse<ResponseResult>) => {
    // @ts-ignore
    if (typeof response.data === "string" && response.data.search(/502/) !== -1) {
      ElMessage({
        message: "服务器正在更新，请稍后...",
        type: "warning",
        showClose: true
      })
      
      return response
    }
    
    if (response.data.code !== ResponseCodeEnum.SUCCESS) {
      // 密码错误
      // if (response.data.code === ResponseCodeEnum.PASSWORD_ERROR) {
      //   console.log("密码错误")
      //   return response
      // }
      // token 过期
      if (response.data.code === ResponseCodeEnum.TOKEN_INVALID) {
        if (!isTokenInvalid) {
          ElMessageBox.confirm("身份验证已过期！您可以取消停留在此页面，或者重新登录", "注意", {
            confirmButtonText: "重新登录",
            cancelButtonText: "取消",
            type: "warning"
          })
            .then(() => {
              isTokenInvalid = true
              
              localStorage.removeItem(StorageKeysEnum.TOKEN)
              localStorage.removeItem(StorageKeysEnum.USER_INFO)
              router.push({ path: "/login" })
              setTimeout(() => {
                isTokenInvalid = false
              }, 1000)
            })
        }
      }
      // 快手未授权
      else if (response.data.code === ResponseCodeEnum.UNAUTHORIZED) {
        return Promise.reject(response.data.message)
      }
      else {
        ElMessage({
          type: "error",
          showClose: true,
          message: response.data.message
        })
      }
    }
    return response
  },
  
  error => {
    if (error.message.includes("timeout")) {
      ElMessage({
        type: "error",
        showClose: true,
        message: "响应超时, 请稍后重试"
      })
    }
    handleError(error)
    return Promise.reject(error)
  }
)

export default instance
